const AUTH_TOKEN_KEY = "authToken";
const REFRESH_TOKEN_KEY = "refreshToken";
const ORG_ID_KEY = "orgId";
const ORG_SUBDOMAIN_KEY = "orgSubdomain";
const ROLE_KEY = "role";
// Security fix (Points 4 & 5): non-sensitive flag only — actual tokens live in httpOnly cookies xyz
const SESSION_ACTIVE_KEY = "sessionActive";

const removeKey = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage access failures and continue best-effort cleanup.
  }
};

const setKey = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage access failures and continue best-effort persistence.
  }
};

const getKey = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const normalizeNumber = (value) => {
  if (value === null || value === undefined || value === "" || value === "null" || value === "undefined") {
    return null;
  }

  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

export const normalizeRoles = (roleValue) => {
  if (Array.isArray(roleValue)) {
    return roleValue.filter(Boolean);
  }

  if (!roleValue) {
    return [];
  }

  if (typeof roleValue === "string") {
    try {
      const parsed = JSON.parse(roleValue);
      return Array.isArray(parsed) ? parsed.filter(Boolean) : [parsed].filter(Boolean);
    } catch (error) {
      return [roleValue];
    }
  }

  return [roleValue].filter(Boolean);
};

// Security fix (Point 4): auth token lives exclusively in the httpOnly cookie.
// Returning null prevents JS-accessible exposure; the browser sends the cookie
// automatically via withCredentials: true on every API request.
export const getStoredAuthToken = () => null;

// Security fix (Point 5): refresh token lives exclusively in the httpOnly cookie.
// Never read it from JS-accessible storage.
export const getStoredRefreshToken = () => null;

export const getStoredOrgId = () =>
  getKey(sessionStorage, ORG_ID_KEY) || getKey(localStorage, ORG_ID_KEY);

export const getStoredOrganizationSubdomain = () =>
  getKey(sessionStorage, ORG_SUBDOMAIN_KEY) || getKey(localStorage, ORG_SUBDOMAIN_KEY) || null;

export const getStoredRoles = () =>
  normalizeRoles(getKey(sessionStorage, ROLE_KEY) || getKey(localStorage, ROLE_KEY));

// Security fix: check non-sensitive session flag instead of token value
export const hasStoredSessionToken = () =>
  Boolean(getKey(sessionStorage, SESSION_ACTIVE_KEY));

export const getStoredSession = () => {
  // Security fix: derive token presence from the non-sensitive session flag.
  // Actual tokens are in httpOnly cookies — never expose them from JS storage.
  const isSessionActive = Boolean(getKey(sessionStorage, SESSION_ACTIVE_KEY));
  const token = isSessionActive ? "session-present" : null;
  const refreshToken = isSessionActive ? "session-present" : null;
  const organizationId = normalizeNumber(getStoredOrgId());
  const organizationSubdomain = getStoredOrganizationSubdomain();
  const role = getStoredRoles();

  return {
    token,
    refreshToken,
    organizationId,
    organizationSubdomain,
    role,
    requiresOrganizationSetup: Boolean(token) && organizationId == null,
  };
};

export const persistStoredSession = (sessionOrToken) => {
  if (typeof sessionOrToken === "string") {
    const tokenVal = sessionOrToken ? "session-present" : "";
    setKey(sessionStorage, AUTH_TOKEN_KEY, tokenVal);
    removeKey(localStorage, AUTH_TOKEN_KEY);
    return;
  }

  const session = sessionOrToken || {};

  // Security fix (Points 4 & 5): tokens are stored only in the server-set httpOnly cookie.
  // Remove any stale values from JS-accessible storage.
  removeKey(sessionStorage, AUTH_TOKEN_KEY);
  removeKey(localStorage, AUTH_TOKEN_KEY);
  removeKey(sessionStorage, REFRESH_TOKEN_KEY);
  removeKey(localStorage, REFRESH_TOKEN_KEY);
  // Store only a non-sensitive session-presence flag for UI routing decisions.
  if (session.token) {
    setKey(sessionStorage, SESSION_ACTIVE_KEY, "true");
  } else {
    removeKey(sessionStorage, SESSION_ACTIVE_KEY);
  }

  if (session.organizationId != null) {
    const orgValue = String(session.organizationId);
    setKey(sessionStorage, ORG_ID_KEY, orgValue);
    setKey(localStorage, ORG_ID_KEY, orgValue);
  } else {
    removeKey(sessionStorage, ORG_ID_KEY);
    removeKey(localStorage, ORG_ID_KEY);
  }

  if (session.organizationSubdomain) {
    setKey(sessionStorage, ORG_SUBDOMAIN_KEY, session.organizationSubdomain);
    setKey(localStorage, ORG_SUBDOMAIN_KEY, session.organizationSubdomain);
  } else {
    removeKey(sessionStorage, ORG_SUBDOMAIN_KEY);
    removeKey(localStorage, ORG_SUBDOMAIN_KEY);
  }

  const roleValue = JSON.stringify(session.role || []);
  setKey(sessionStorage, ROLE_KEY, roleValue);
  removeKey(localStorage, ROLE_KEY);
};

export const updateStoredTokens = ({ accessToken, refreshToken }) => {
  // Security fix: tokens are in httpOnly cookies — clear any stale storage values.
  removeKey(sessionStorage, AUTH_TOKEN_KEY);
  removeKey(localStorage, AUTH_TOKEN_KEY);
  if (refreshToken) {
    removeKey(sessionStorage, REFRESH_TOKEN_KEY);
  }
  // Update only the non-sensitive session-active flag.
  if (accessToken) {
    setKey(sessionStorage, SESSION_ACTIVE_KEY, "true");
  }
};

export const clearStoredSession = () => {
  [
    AUTH_TOKEN_KEY,
    REFRESH_TOKEN_KEY,
    ORG_ID_KEY,
    ORG_SUBDOMAIN_KEY,
    ROLE_KEY,
    SESSION_ACTIVE_KEY,
  ].forEach((key) => {
    removeKey(sessionStorage, key);
    removeKey(localStorage, key);
  });
};
